import { View, DefinitionItem, DefinitionList, Address } from "components/Shared";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map } from "components/Widgets";

interface Props {
	propertyId: string | number;
	link?: string;
}

// The property (and its map + addresses) is shared by every tenancy on a
// project, so this panel is rendered ONCE by TenanciesTab rather than once per
// tenancy. Previously this lived inside TenancyTab, which duplicated the map
// for each tenancy of the same property.
const PropertyPanel = ({ propertyId, link }: Props) => {
	const formatBuildings = (buildings: any[]) =>
		_.map(buildings, building => ({
			name: building.name,
			coordinates: building.shape,
			area: building.area
		}));
	const [property, setProperty] = useState([] as any[]);
	const [addresses, setAddresses] = useState([] as any[]);
	const [buildings, setBuildings] = useState([] as any[]);
	const [propertyDatasource] = useState(new Datasource({ mainModelName: "property" }));
	const [buildingDatasource] = useState(new Datasource({ mainModelName: "building" }));
	const [addressDatasource] = useState(new Datasource({ mainModelName: "address" }));

	useEffect(() => {
		if (_.isNil(propertyId)) return;
		let sub = propertyDatasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setProperty(response.normalizedMainModelResponse)
		);
		return () => sub.unsubscribe();
	}, [propertyDatasource, propertyId]);

	useEffect(() => {
		let sub = buildingDatasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setBuildings(formatBuildings(response.normalizedMainModelResponse))
		);
		return () => sub.unsubscribe();
	}, [buildingDatasource]);

	useEffect(() => {
		let sub = addressDatasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAddresses(response.normalizedMainModelResponse)
		);
		return () => sub.unsubscribe();
	}, [addressDatasource]);

	/******************************************  API Call  ******************************************/
	useEffect(() => {
		if (!_.isEmpty(propertyId))
			propertyDatasource.get("v2", `properties/${propertyId}`, {
				queryStringParameters: {
					fields: JSON.stringify({
						property: ["name", "owned_by", "managed_by", "operated_by"]
					})
				}
			});
	}, [propertyDatasource, propertyId]);

	useEffect(() => {
		if (!_.isEmpty(propertyId))
			buildingDatasource.get("v2", "buildings", {
				queryStringParameters: {
					property_id: propertyId,
					per_page: 1000,
					building: ["name", "area", "shape"]
				}
			});
	}, [buildingDatasource, propertyId]);

	useEffect(() => {
		if (!_.isEmpty(propertyId))
			addressDatasource.get("v2", "addresses", { queryStringParameters: { property_id: propertyId, per_page: 1000 } });
	}, [addressDatasource, propertyId]);

	return (
		<View>
			<h4>
				{!!link ? <Link to={`/properties/${propertyId}`}>{_.get(property, "name")}</Link> : _.get(property, "name")}
			</h4>
			<DefinitionList>
				{!!_.get(property, "owned_by") && (
					<DefinitionItem title="owned_by">{_.get(property, "owned_by")!.name}</DefinitionItem>
				)}
				{!!_.get(property, "managed_by") && (
					<DefinitionItem title="managed_by">{_.get(property, "managed_by")!.name}</DefinitionItem>
				)}
				{!!_.get(property, "operated_by") && (
					<DefinitionItem title="developed_by">{_.get(property, "operated_by")!.name}</DefinitionItem>
				)}
			</DefinitionList>
			<Map addresses={addresses} buildings={buildings} height="394px" />
			<hr />
			<DefinitionList>
				{_.map(addresses, a => (
					<DefinitionItem key={_.get(a, "locality_name")} title={_.get(a, "locality_name")}>
						<Address key={a.id} address={a} />
					</DefinitionItem>
				))}
			</DefinitionList>
		</View>
	);
};

export default memo(PropertyPanel);
